import { useEffect, useState } from 'react';
import { getMaintenanceTasks, saveMaintenanceTask, deleteMaintenanceTask, GetMaintenanceTasksOutputType } from 'zite-endpoints-sdk';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Pencil, Trash2, Loader2, Wrench, AlertCircle, CheckCircle2, Clock, Filter } from 'lucide-react';
import { format, parseISO } from 'date-fns';

type Task = GetMaintenanceTasksOutputType['tasks'][0];

const PRIORITY_COLORS = { Urgent: 'bg-red-500/10 text-red-700 border-red-500/30', High: 'bg-orange-500/10 text-orange-700 border-orange-500/30', Medium: 'bg-yellow-500/10 text-yellow-700 border-yellow-500/30', Low: 'bg-muted text-muted-foreground' };
const STATUS_CONFIG = { Open: { icon: AlertCircle, cls: 'text-blue-600' }, 'In Progress': { icon: Clock, cls: 'text-yellow-600' }, Resolved: { icon: CheckCircle2, cls: 'text-green-600' }, "Won't Fix": { icon: AlertCircle, cls: 'text-muted-foreground' } };

const fmtDt = (d?: string) => { try { return d ? format(parseISO(d), 'dd MMM yyyy HH:mm') : '—'; } catch { return d || '—'; } };

export default function MaintenanceAdminTab() {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [counts, setCounts] = useState({ open: 0, inProgress: 0, resolved: 0, urgent: 0 });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Task | null>(null);
  const [saving, setSaving] = useState(false);
  const [filterStatus, setFilterStatus] = useState('Active');
  const [editForm, setEditForm] = useState<Partial<Task>>({});

  const load = () => {
    setLoading(true);
    getMaintenanceTasks({}).then(d => { setTasks(d.tasks); setCounts(d.counts); }).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openTask = (task: Task) => { setSelected(task); setEditForm({ status: task.status, assignedTo: task.assignedTo, resolutionNotes: task.resolutionNotes }); };

  const handleUpdate = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await saveMaintenanceTask({ id: selected.id, title: selected.title, ...editForm });
      toast.success('Task updated');
      setSelected(null);
      load();
    } catch (e: any) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id: string) => {
    try { await deleteMaintenanceTask({ id }); toast.success('Deleted'); load(); }
    catch (e: any) { toast.error(e.message); }
  };

  const displayed = filterStatus === 'Active'
    ? tasks.filter(t => t.status === 'Open' || t.status === 'In Progress')
    : filterStatus === 'Resolved'
    ? tasks.filter(t => t.status === 'Resolved' || t.status === "Won't Fix")
    : tasks;

  if (loading) return <div className="space-y-3">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-14" />)}</div>;

  return (
    <div className="space-y-4">
      {/* Stats */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: 'Open', value: counts.open, cls: 'text-blue-600' },
          { label: 'In Progress', value: counts.inProgress, cls: 'text-yellow-600' },
          { label: 'Urgent', value: counts.urgent, cls: 'text-red-600' },
          { label: 'Resolved', value: counts.resolved, cls: 'text-green-600' },
        ].map(s => (
          <div key={s.label} className="border border-border rounded-lg p-3 text-center">
            <p className={`text-2xl font-bold ${s.cls}`}>{s.value}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Filter */}
      <div className="flex items-center gap-2">
        <Filter className="w-3.5 h-3.5 text-muted-foreground" />
        {['Active', 'Resolved', 'All'].map(f => (
          <button key={f} onClick={() => setFilterStatus(f)}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${filterStatus === f ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}>
            {f}
          </button>
        ))}
      </div>

      {displayed.length === 0 ? (
        <div className="border border-dashed border-border rounded-xl p-12 text-center text-muted-foreground">
          <Wrench className="w-8 h-8 mx-auto mb-3 opacity-30" />
          <p className="text-sm">No maintenance tasks {filterStatus === 'Active' ? 'open' : 'found'}.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {displayed.map(task => {
            const statusCfg = STATUS_CONFIG[task.status as keyof typeof STATUS_CONFIG] || STATUS_CONFIG.Open;
            const StatusIcon = statusCfg.icon;
            return (
              <div key={task.id} className="border border-border rounded-lg p-3 flex items-start gap-3 hover:bg-muted/20 cursor-pointer" onClick={() => openTask(task)}>
                <StatusIcon className={`w-4 h-4 mt-0.5 shrink-0 ${statusCfg.cls}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-0.5">
                    <span className="font-medium text-foreground text-sm">{task.title}</span>
                    {task.priority && <Badge variant="outline" className={`text-xs ${PRIORITY_COLORS[task.priority as keyof typeof PRIORITY_COLORS] || ''}`}>{task.priority}</Badge>}
                    <Badge variant="outline" className="text-xs">{task.status}</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {task.category && <span>{task.category} · </span>}
                    {task.location && <span>{task.location} · </span>}
                    {task.reporterName && <span>Reported by {task.reporterName} · </span>}
                    {fmtDt(task.reportedAt)}
                  </p>
                  {task.assignedTo && <p className="text-xs text-muted-foreground mt-0.5">Assigned to: {task.assignedTo}</p>}
                </div>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive hover:text-destructive shrink-0"
                      onClick={e => e.stopPropagation()}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader><AlertDialogTitle>Delete this task?</AlertDialogTitle></AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => handleDelete(task.id)} className="bg-destructive text-destructive-foreground">Delete</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            );
          })}
        </div>
      )}

      {/* Edit Sheet */}
      <Sheet open={!!selected} onOpenChange={open => !open && setSelected(null)}>
        <SheetContent className="sm:max-w-md overflow-y-auto">
          {selected && (
            <>
              <SheetHeader className="mb-4">
                <SheetTitle>{selected.title}</SheetTitle>
              </SheetHeader>
              <div className="space-y-4 text-sm">
                <div className="grid grid-cols-2 gap-3 p-3 bg-muted/30 rounded-lg">
                  {selected.category && <div><span className="text-xs text-muted-foreground block">Category</span>{selected.category}</div>}
                  {selected.location && <div><span className="text-xs text-muted-foreground block">Location</span>{selected.location}</div>}
                  {selected.reporterName && <div><span className="text-xs text-muted-foreground block">Reported by</span>{selected.reporterName}</div>}
                  {selected.reportedAt && <div><span className="text-xs text-muted-foreground block">Reported</span>{fmtDt(selected.reportedAt)}</div>}
                </div>
                {selected.description && <div><p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Description</p><p className="text-sm text-foreground leading-relaxed">{selected.description}</p></div>}
                <div>
                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 block">Status</label>
                  <Select value={editForm.status || 'Open'} onValueChange={v => setEditForm(f => ({ ...f, status: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Open">Open</SelectItem>
                      <SelectItem value="In Progress">In Progress</SelectItem>
                      <SelectItem value="Resolved">Resolved</SelectItem>
                      <SelectItem value="Won't Fix">Won't Fix</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 block">Assigned To</label>
                  <Input value={editForm.assignedTo || ''} onChange={e => setEditForm(f => ({ ...f, assignedTo: e.target.value }))} placeholder="Name or team" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 block">Resolution Notes</label>
                  <Textarea value={editForm.resolutionNotes || ''} onChange={e => setEditForm(f => ({ ...f, resolutionNotes: e.target.value }))} rows={3} placeholder="What was done to fix this..." />
                </div>
                <Button className="w-full" onClick={handleUpdate} disabled={saving}>
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Changes'}
                </Button>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
